// swimlaneChrome — the lane chrome for the swimlane board: the sticky lane
// header row (module labels + "colour by" tint + right-click menu) and the
// dashed lane separators. Pure presentational; geometry is just the lane grid.

import type { Lane } from "@archlang/engine";
import {
  LANE_WIDTH,
  LANE_HEADER_H,
  LANE_HEADER_CHIP_INSET_X,
  LANE_HEADER_CHIP_INSET_Y,
  LANE_HEADER_CHIP_BORDER,
  LANE_HEADER_CHIP_PAD_X,
  LANE_HEADER_FONT_SIZE,
  LANE_HEADER_LINE_H,
  BOARD_RIGHT_PAD,
} from "@archlang/scene/process";
import { useContextMenu } from "@archlang/ui";

export function LaneHeaders({
  lanes,
  labelLines,
  onSelectModule,
  laneColor,
}: {
  lanes: readonly Lane[];
  /** Pre-wrapped header lines per lane id, from the scene geometry pass — the
   *  SAME lines the headless export draws. A long module name wraps/ellipsizes
   *  here instead of spilling out of its chip. */
  labelLines?: ReadonlyMap<string, readonly string[]>;
  onSelectModule: (q: string) => void;
  /** "Colour by" tint per lane id (module lanes only). */
  laneColor?: ReadonlyMap<string, string>;
}) {
  const menu = useContextMenu();
  return (
    <div
      className="sticky top-0 left-0"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${Math.max(lanes.length, 1)}, ${LANE_WIDTH}px)`,
        height: LANE_HEADER_H,
        // Match the board container's total width (lanes + right pad) so the
        // header and lanes share one scroll extent and never drift apart.
        width: Math.max(lanes.length, 1) * LANE_WIDTH + BOARD_RIGHT_PAD,
        background: "rgb(var(--canvas))",
        zIndex: 5,
      }}
    >
      {lanes.map((l) => {
        const tint = laneColor?.get(l.id);
        const qual = l.qualifiedPath.join(".");
        const lines = labelLines?.get(l.id) ?? [l.label];
        return (
          <div
            key={l.id}
            onClick={() => onSelectModule(qual)}
            onContextMenu={(e) =>
              menu.open(e, [
                { label: "Open module", onClick: () => onSelectModule(qual) },
                {
                  label: "Copy module name",
                  onClick: () => void navigator.clipboard?.writeText(qual),
                },
              ])
            }
            className="flex items-center justify-center cursor-pointer"
            style={{ padding: `${LANE_HEADER_CHIP_INSET_Y}px ${LANE_HEADER_CHIP_INSET_X}px` }}
          >
            <div
              data-lane-colored={tint ? true : undefined}
              title={l.label}
              className="w-full text-center flex flex-col justify-center"
              style={{
                background: "rgb(var(--panel))",
                border: `${LANE_HEADER_CHIP_BORDER}px solid ${tint ?? "rgb(var(--border))"}`,
                // The tint doubles as a thicker footer accent so the lane's
                // colour reads even when the border is subtle.
                boxShadow: tint ? `inset 0 -2px 0 0 ${tint}` : undefined,
                borderRadius: 6,
                color: "rgb(var(--text))",
                fontSize: LANE_HEADER_FONT_SIZE,
                // Explicit box + line height: the wrapped lines come from the
                // geometry pass, so the chip must stack them at the same pitch
                // the headless export does (never browser-measured).
                boxSizing: "border-box",
                height: LANE_HEADER_H - 2 * LANE_HEADER_CHIP_INSET_Y,
                lineHeight: `${LANE_HEADER_LINE_H}px`,
                padding: `0 ${LANE_HEADER_CHIP_PAD_X}px`,
              }}
            >
              {lines.map((ln, i) => (
                <div key={i} style={{ whiteSpace: "nowrap" }}>
                  {ln}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {menu.node}
    </div>
  );
}

export function LaneSeparators({ count, bottom }: { count: number; bottom: number }) {
  const lines: number[] = [];
  for (let i = 1; i < count; i++) lines.push(i * LANE_WIDTH);
  return (
    <g>
      {lines.map((x) => (
        <line
          key={x}
          x1={x}
          y1={LANE_HEADER_H}
          x2={x}
          y2={bottom}
          stroke="#1e293b"
          strokeDasharray="3 4"
        />
      ))}
    </g>
  );
}
